import { inject, Injectable, Injector, Type } from '@angular/core';
import { Dialog, DialogConfig, DialogRef } from '@angular/cdk/dialog';
import { Observable } from 'rxjs';

export { DIALOG_DATA as HLM_DIALOG_DATA } from '@angular/cdk/dialog';

/**
 * Thin wrapper over CDK Dialog — mirrors MatDialog.open() API so ports are mechanical.
 *
 * Returned ref adds `.afterClosed()` (compat alias for `.closed`).
 */
export interface HlmDialogConfig<D = unknown> extends DialogConfig<D> {
  panelClass?: string | string[];
}

export type HlmDialogRef<R = unknown, C = unknown> = DialogRef<R, C> & {
  afterClosed(): Observable<R | undefined>;
};

@Injectable({ providedIn: 'root' })
export class HlmDialogService {
  private readonly cdkDialog = inject(Dialog);

  /**
   * Open a dialog. Returns a ref with MatDialog-style `.afterClosed()` convenience.
   */
  open<R = unknown, D = unknown, C = unknown>(
    component: Type<C>,
    config: HlmDialogConfig<D> = {},
  ): HlmDialogRef<R, C> {
    const merged: DialogConfig<D> = {
      ...config,
      backdropClass: mergeClasses(config.backdropClass, 'bg-foreground/35 backdrop-blur-sm'),
      panelClass: mergeClasses(config.panelClass, 'outline-none'),
      hasBackdrop: config.hasBackdrop ?? true,
      autoFocus: config.autoFocus ?? 'first-tabbable',
      restoreFocus: config.restoreFocus ?? true,
    };

    const ref = this.cdkDialog.open<R, D, C>(component, merged);
    (ref as HlmDialogRef<R, C>).afterClosed = () => ref.closed as Observable<R | undefined>;
    return ref as HlmDialogRef<R, C>;
  }
}

function mergeClasses(
  a: string | string[] | undefined,
  b: string | string[] | undefined,
): string[] {
  const toArr = (v: string | string[] | undefined): string[] =>
    v ? (Array.isArray(v) ? v : [v]) : [];
  return [...toArr(a), ...toArr(b)];
}
